import { useCallback } from 'react'
import { CLICK_PREVENTION_DELAY_MS } from '../constants/shape-manager-constants'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { ShapeUpdatesApi } from './use-shape-updates'

export function useInteractionControl(ctx: ShapeManagerStateApi & ShapeUpdatesApi) {
  const {
    handlePointerMoveBridge,
    handlePointerUpBridge,
    interactionEndTimeRef,
    interactionRef,
    onCornerResizeGestureActiveChangeRef,
    pendingPointerRef,
    rafIdRef,
    savedBodyCursorForMultiMoveRef,
    scheduleLiveSelectionGeometrySync,
    scheduleLiveWrapSync,
    setIsResizeGestureActive,
    updateShapeRef,
  } = ctx

  const isInteracting = useCallback(() => {
    const timeSinceInteractionEnd = Date.now() - interactionEndTimeRef.current

    return interactionRef.current !== null || timeSinceInteractionEnd < CLICK_PREVENTION_DELAY_MS
  }, [interactionEndTimeRef, interactionRef])

  /**
   * Ends the active gesture: clears interaction state, cancels any pending animation frame, stamps
   * the end time (`isInteracting`'s click-prevention window) and detaches the window listeners.
   * `restoreBodyCursor` restores the cursor saved at gesture start, at the same point the cancel
   * paths always did; pointer-up restores it in its own `finally` instead.
   */
  const finishInteraction = useCallback(
    (opts?: { restoreBodyCursor?: boolean }) => {
      interactionRef.current = null
      setIsResizeGestureActive(false)
      onCornerResizeGestureActiveChangeRef.current?.(false)
      pendingPointerRef.current = null

      if (rafIdRef.current != null) {
        window.cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }

      if (opts?.restoreBodyCursor && savedBodyCursorForMultiMoveRef.current !== null) {
        document.body.style.cursor = savedBodyCursorForMultiMoveRef.current
        savedBodyCursorForMultiMoveRef.current = null
      }

      interactionEndTimeRef.current = Date.now()

      window.removeEventListener('pointermove', handlePointerMoveBridge)
      window.removeEventListener('pointerup', handlePointerUpBridge, true)
      window.removeEventListener('pointercancel', handlePointerUpBridge, true)
    },
    [
      handlePointerMoveBridge,
      handlePointerUpBridge,
      interactionEndTimeRef,
      interactionRef,
      onCornerResizeGestureActiveChangeRef,
      pendingPointerRef,
      rafIdRef,
      savedBodyCursorForMultiMoveRef,
      setIsResizeGestureActive,
    ]
  )

  /**
   * Cancels an in-progress single-shape resize gesture (Escape key), reverting the shape to its
   * pre-drag snapshot without committing/persisting anything. No-ops (returns false) for any
   * other interaction type, or a multi-shape resize (`unifiedSelectionResizeSession`) — only the
   * single-shape resize path is covered today.
   */
  const cancelActiveResize = useCallback((): boolean => {
    const interaction = interactionRef.current

    if (
      !interaction ||
      interaction.type !== 'resize' ||
      interaction.unifiedSelectionResizeSession
    ) {
      return false
    }

    // Full snapshot restore (including path/pathCommands) so a custom path reverts exactly
    // rather than relying on applyPartialShapeUpdate's multiplicative rescale.
    updateShapeRef.current(interaction.shapeId, interaction.initial)

    finishInteraction({ restoreBodyCursor: true })

    scheduleLiveSelectionGeometrySync()
    scheduleLiveWrapSync()

    return true
  }, [
    finishInteraction,
    interactionRef,
    scheduleLiveSelectionGeometrySync,
    scheduleLiveWrapSync,
    updateShapeRef,
  ])

  const cancelActiveRotate = useCallback((): boolean => {
    const interaction = interactionRef.current

    if (!interaction || interaction.type !== 'rotate' || interaction.multiRotateInitials) {
      return false
    }

    // Full snapshot restore (same reasoning as cancelActiveResize above): if the shape is a
    // Group, updateShape's existing group-transform-propagation cascades the revert to
    // descendant shapes/text boxes automatically — the same mechanism a live rotate tick
    // already relies on, so no extra code is needed for that case.
    updateShapeRef.current(interaction.shapeId, interaction.initial)

    finishInteraction({ restoreBodyCursor: true })

    scheduleLiveSelectionGeometrySync()
    scheduleLiveWrapSync()

    return true
  }, [
    finishInteraction,
    interactionRef,
    scheduleLiveSelectionGeometrySync,
    scheduleLiveWrapSync,
    updateShapeRef,
  ])

  return { isInteracting, finishInteraction, cancelActiveResize, cancelActiveRotate }
}

export type InteractionControlApi = ReturnType<typeof useInteractionControl>
